import React, { useContext, useState, useEffect } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { SecondaryButton, ButtonIcon, StandAloneLink, showToastNotification } from '@hz-design-system/web-ui';
import Cookies from 'js-cookie';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import toggleFavouriteListing from '../../repositories/toggleFavouriteListing';
import { trackGAEvent } from '../../../../utils/gaTracking';

import { TListingClient } from 'types/TListing';

import Classes from './FavouriteButton.scss';
import BuyerInfoModal from './FavouritesConnect/BuyerInfoModal';

type TFavouriteButtonProps = {
  itemId: TListingClient['itemId'];
  isSaved: TListingClient['isSaved'];
  asLink?: boolean;
  favoritedCount?: number;
  isDisplayedOnMobile?: boolean;
};

type TFavoriteEvent = {
  state: boolean;
  count: number;
};

const addToFavoritesBeginEvents = {
  label: 'WatchlistAddBegin',
  action: 'Top | Bottom',
};
const addToFavoritesSuccessEvents = {
  label: 'WatchlistAddSuccess',
  action: 'Top | Bottom',
};
const removeFromFavoritesBeginEvents = {
  label: 'WatchlistRemoveBegin',
  action: 'Top | Bottom',
};
const removeFromFavoritesSuccessEvents = {
  label: 'WatchlistRemoveSuccess',
  action: 'Top | Bottom',
};

const getGaEventsForBegin = (isFavorite) => (isFavorite ? removeFromFavoritesBeginEvents : addToFavoritesBeginEvents);
const getGaEventsForSuccess = (isFavorite) =>
  isFavorite ? removeFromFavoritesSuccessEvents : addToFavoritesSuccessEvents;

const trackGaEventForBegin = (isFavourite) => {
  const { label, action } = getGaEventsForBegin(isFavourite);
  trackGAEvent(label, action);
};

const trackGaEventForSuccess = (isFavourite) => {
  const { label, action } = getGaEventsForSuccess(isFavourite);
  trackGAEvent(label, action);
};

let LoginDialogContainer;

const FavouriteButton = ({
  isSaved,
  itemId,
  asLink = false,
  favoritedCount = 0,
  isDisplayedOnMobile,
}: TFavouriteButtonProps) => {
  const { userLoggedIn, xsrfToken, tenantContext, experiments = {} } = useContext(EnvironmentContext);
  const { t } = useI18nContext();
  const [isFavourite, setIsFavourite] = useState(isSaved);
  const [favouriteCount, setFavouriteCount] = useState(favoritedCount);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showFavouriteInfoModal, setShowFavouriteInfoModal] = useState(false);
  const { isFavouritesConnectEnabled } = experiments;

  const toggleFavourite = async () => {
    const newState = !isFavourite;
    // optimistic update
    setShowLoginDialog(false);
    setIsFavourite(newState);
    const event = new CustomEvent('favoriteToggled', {
      detail: {
        state: newState,
        count: newState ? favouriteCount + 1 : favouriteCount - 1,
      },
    });
    window.dispatchEvent(event);

    trackGaEventForBegin(isFavourite);

    const response = await toggleFavouriteListing({ itemId, favourite: newState, xsrfToken });

    // success state
    if (isFavourite !== response.isFavourite) {
      trackGaEventForSuccess(isFavourite);

      showToastNotification(
        'success',
        t(isFavourite ? 'listing.favourite.notifications.unfavoriting' : 'listing.favourite.notifications.favoriting'),
      );
    } else {
      showToastNotification('error', t('error.generic'));
    }

    // actual update
    setIsFavourite(response.isFavourite);

    if (response.isFavourite) {
      setShowFavouriteInfoModal(true);
    }
  };

  const handleFavouriteToggle = async () => {
    if (userLoggedIn) {
      await toggleFavourite();
    } else {
      LoginDialogContainer = (
        await import(
          /* webpackChunkName: "loginDialogContainer" */ '../../../../components/LoginDialog/LoginDialogContainer'
        )
      ).default;
      setShowLoginDialog(true);
    }
  };

  const handleLoginSuccess = () => {
    Cookies.set('favoriteToggleReloaded', 'true');
    document.location.reload();
  };

  const handleFavoriteEvent = ({ detail: { count, state } }: CustomEvent<TFavoriteEvent>) => {
    setIsFavourite(state);
    setFavouriteCount(count);
  };

  useEffect(() => {
    // if isAdOwner we remove the cookie before redirecting to SVIP in checkListingAvailabilityMiddleware.
    const favouriteFlag = Cookies.get('favoriteToggleReloaded');
    if (favouriteFlag) {
      handleFavouriteToggle();
      Cookies.remove('favoriteToggleReloaded');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('favoriteToggled', handleFavoriteEvent as EventListener, false);
    return () => window.removeEventListener('favoriteToggled', handleFavoriteEvent as EventListener, false);
  }, []);

  return (
    <>
      {asLink && (
        <StandAloneLink
          className={`${Classes.asLink} ${isDisplayedOnMobile ? Classes.showOnMobile : ''}`}
          iconLeft={isFavourite ? 'heart-filled' : 'heart'}
          onClick={handleFavouriteToggle}
          aria-pressed={isFavourite}
          aria-label={t(isFavourite ? 'listing.favourite.saved' : 'listing.favourite.save')}
        >
          <Trans tagName="span" i18nKey={isFavourite ? 'listing.favourite.saved' : 'listing.favourite.save'} />
        </StandAloneLink>
      )}
      {!asLink && (
        <SecondaryButton
          className={Classes.root}
          onClick={handleFavouriteToggle}
          aria-pressed={isFavourite}
          aria-label={t(isFavourite ? 'listing.favourite.saved' : 'listing.favourite.save')}
        >
          <ButtonIcon name={isFavourite ? 'heart-filled' : 'heart'} isStartIcon={false} />
          {Boolean(favouriteCount) && <span className={Classes.favouriteCount}>{favouriteCount}</span>}
        </SecondaryButton>
      )}
      {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
      {isFavouritesConnectEnabled && showFavouriteInfoModal && (
        <BuyerInfoModal onClose={() => setShowFavouriteInfoModal(false)} tenant={tenantContext?.tenant} />
      )}
    </>
  );
};

export default FavouriteButton;
